import { queryClient } from '@m43/core';
import { useApiClient } from '~/api-client';
import { BoloResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { runInAction } from 'mobx';
import { useEnabledFeatures } from '../../common/hooks/enabled-features';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';
import { useBoloQueueViews } from './bolo-queue-views-provider';
import { useAudibleAlerts } from '../../sounds/play-sound-provider';
import { useApplicationType } from '../../common/hooks/use-application-type';

const BOLO_QUEUE_VIEWS_BOOTSTRAP_QUERY_KEY = 'bolo-queue-views-bootstrap';

export function useIntegratedBoloQueueViews() {
    const { boloQueueViewsRef, getListeners } = useBoloQueueViews();
    const enabledFeatures = useEnabledFeatures();
    const { isCad } = useApplicationType();
    const apiClient = useApiClient();
    const { playAlert } = useAudibleAlerts();

    const bootstrapQuery = useQuery({
        queryKey: [BOLO_QUEUE_VIEWS_BOOTSTRAP_QUERY_KEY],
        queryFn() {
            return apiClient.request<BoloResource.GetBoloQueueViewsForDepartment>({
                method: 'POST',
                url: 'bolo/queues',
                data: {},
            });
        },
        onSuccess(resp) {
            const boloQueueViews = resp.data.data?.boloQueueViews;
            if (boloQueueViews) {
                boloQueueViewsRef.current = Object.fromEntries(
                    boloQueueViews.map((boloQueueView) => [boloQueueView.id, boloQueueView])
                );
            }
            getListeners().forEach((listener) => listener.onBootstrap());
        },
    });

    useIntegratedStreams({
        query: bootstrapQuery,
        streamNames: ['boloQueues'],
        onMessage(message) {
            let hasNewUrgentBolo = false;
            message.payload.boloQueueViews.forEach((boloQueueView) => {
                if (boloQueueView.isPriority1 && !boloQueueViewsRef.current[boloQueueView.id]) {
                    hasNewUrgentBolo = true;
                }
                boloQueueViewsRef.current[boloQueueView.id] = boloQueueView;
            });
            if (hasNewUrgentBolo) {
                playAlert({ type: 'URGENT_BOLO_RECEIVED', context: { enabledFeatures, isCad } });
            }
            message.payload.expiredBoloIds.forEach((id) => {
                delete boloQueueViewsRef.current[id];
            });
            runInAction(() => {
                getListeners().forEach((listener) => listener.onUpdate(message.payload));
            });
        },
        onReconnect(socketStatusChangeEvent) {
            const messagePosition =
                socketStatusChangeEvent.streamsLastMessageMapBeforeSocketClosure?.['boloQueues']
                    ?.messagePosition;
            if (messagePosition) {
                return apiClient
                    .request<BoloResource.CatchupBoloQueueViews>({
                        method: 'POST',
                        url: 'bolo/queues/catchup',
                        data: {
                            offset: messagePosition,
                        },
                    })
                    .then((response) => {
                        const shouldBootstrap = response.data.data?.shouldBootstrap;
                        const bundles = response.data.data?.boloQueuesBundles;
                        if (shouldBootstrap) {
                            bootstrapQuery.refetch();
                        } else if (bundles) {
                            bundles.forEach((bundle) => {
                                bundle.boloQueueViews.forEach((boloQueueView) => {
                                    boloQueueViewsRef.current[boloQueueView.id] = boloQueueView;
                                });
                                bundle.expiredBoloIds?.forEach((id) => {
                                    delete boloQueueViewsRef.current[id];
                                });
                            });
                            getListeners().forEach((listener) => listener.onBootstrap());
                        }
                    })
                    .catch(() => {
                        queryClient.invalidateQueries([BOLO_QUEUE_VIEWS_BOOTSTRAP_QUERY_KEY]);
                    });
            } else {
                return bootstrapQuery.refetch();
            }
        },
    });
}
